import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getRatingIntFromText } from '../../../../models/UiUtils';
import {
	invalidateGetGoogleBusinessReview,
	invalidateInfiniteReviews,
	usePostGoogleBusinessReply,
} from '../../../../queries';
import { Avatar } from '@HoudiniUI/Avatar';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { LocationPinIcon } from '../../../../web/components/svgs/icons/LocationPinIcon';
import { aceGray, brandPrimary, noteTintColor } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { Dialog, DialogBody, DialogHeader, DialogPopup, DialogTitle } from '../../../components-v2/Dialog';
import { GoogleReviewReplyAssistantChat } from '../../../containers/templates/AssistantChat';
import { Button } from '../../Button';
import { ActiveGoogleBusinessReview } from '../../Happiness/DashboardGoogleBusinessProfileCard';
import { StarRating } from '../../surveys/StarRating';
import { LeftChevron } from '../../svgs/icons/LeftChevron';
import { RightChevron } from '../../svgs/icons/RightChevron';
import { SuccessCheckIcon } from '../../svgs/icons/SuccessCheckIcon';
import { styleSheet } from './styles';

export const GoogleBusinessReplyModal = ({
	isOpen,
	activeReview,
	onCloseModal,
	isLoadingReviews,
	disableNext,
	carouselProgressText,
	disablePrevious,
	goToNext,
	onSuccess: onSuccessFromProps,
	remainingReviewsCount,
	goBack,
}: {
	isOpen: boolean;
	activeReview?: ActiveGoogleBusinessReview;
	onCloseModal(): void;
	isLoadingReviews: boolean;
	goToNext(): void;
	goBack(): void;
	onSuccess: (reviewId: string) => void;
	remainingReviewsCount: number;
	disableNext: boolean;
	disablePrevious: boolean;
	carouselProgressText: string;
}) => {
	const userSession = useUserSession();
	const [response, setResponse] = useState('');
	const [showSuccessScreen, setShowSuccessScreen] = useState(false);
	const [chatSessionId, setChatSessionId] = useState<string | undefined>(undefined);

	const reviewId = activeReview?.id;

	const onSuccess = () => {
		onSuccessFromProps(reviewId);
		setResponse('');
		invalidateGetGoogleBusinessReview(reviewId);
		invalidateInfiniteReviews();
		setShowSuccessScreen(true);
	};

	const postGoogleBusinessReplyMutation = usePostGoogleBusinessReply({
		onSuccess,
	});

	const onPostReplyClicked = () => {
		postGoogleBusinessReplyMutation.reset();
		postGoogleBusinessReplyMutation.mutate({
			comment: response,
			reviewId,
			sessionId: chatSessionId ?? activeReview?.aiGeneratedReply?.sessionId,
		});
	};

	const onCancel = () => {
		setResponse('');
		invalidateGetGoogleBusinessReview(reviewId);
		onCloseModal();
	};

	const closeModal = () => {
		setShowSuccessScreen(false);
		onCloseModal();
	};

	const onTextChanged = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setResponse(event.target.value || '');
	};

	// Prepopulate response with generated reply if includeGeneratedReply is true
	React.useEffect(() => {
		if (activeReview?.includeGeneratedReply && activeReview?.aiGeneratedReply?.comment) {
			setResponse(activeReview.aiGeneratedReply.comment);
		} else {
			setResponse('');
		}
	}, [activeReview?.includeGeneratedReply, activeReview?.aiGeneratedReply?.comment]);

	const getDaysAgoText = (value: any) => {
		return moment(value).fromNow();
	};

	if (!isOpen) {
		return null;
	}

	const getInitial = (value: string) => {
		return value.substring(0, 1);
	};

	const isReplying = postGoogleBusinessReplyMutation.status === 'pending';
	const totalStars = 5;

	const renderReviewContent = () => {
		if (isLoadingReviews || !activeReview) {
			return <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='small' />;
		}

		const rating = getRatingIntFromText(activeReview.rating);
		const reviewText = activeReview.text || '';

		return (
			<>
				<div className={css(styleSheet.reviewRow)}>
					<button
						onClick={goBack}
						className={css(
							styleSheet.navIconSection,
							styleSheet.borderRight,
							disablePrevious && baseStyleSheet.disabled
						)}
					>
						<LeftChevron height={40} width={40} colorFill={aceGray} />
					</button>
					<article className={css(styleSheet.reviewerColumn)}>
						<div className={css(styleSheet.responsesCount)}>{carouselProgressText}</div>
						<div className={css(styleSheet.reviewer)}>
							<div>
								<Avatar
									fallbackText={getInitial(activeReview.reviewer.name)}
									imgSrc={activeReview.reviewer.profilePhotoUrl}
									styles={[styleSheet.avatar]}
									fallbackStyles={[styleSheet.avatar]}
								/>
							</div>

							<div className={css(styleSheet.author)}>
								<span
									className={css(styleSheet.reviewerName)}
									title={activeReview.reviewer.name}
									style={{
										WebkitLineClamp: 2,
										WebkitBoxOrient: 'vertical',
										display: '-webkit-box',
										overflow: 'hidden',
									}}
								>
									{activeReview.reviewer.name}
								</span>

								<em className={css(styleSheet.timeAgo)}>Posted {getDaysAgoText(activeReview.updateTime)}</em>
							</div>
						</div>

						<div className={css(styleSheet.location)}>
							<LocationPinIcon className={css(styleSheet.locationIcon)} fillColor={brandPrimary} />
							{activeReview.locationDescription}
						</div>
					</article>

					<article className={css(styleSheet.reviewColumn)}>
						<div className={css(styleSheet.rating)}>
							<StarRating
								readonly
								fillColor={noteTintColor}
								total={totalStars}
								value={rating}
								starStyles={[styleSheet.ratingStar]}
							/>
						</div>

						<p className={css(styleSheet.reviewText)}>
							{reviewText || <i className={css(styleSheet.emptyComment)}>no comment added</i>}
						</p>
					</article>
					<button
						onClick={goToNext}
						className={css(styleSheet.navIconSection, styleSheet.borderLeft, disableNext && baseStyleSheet.disabled)}
					>
						<RightChevron height={40} width={40} color={aceGray} />
					</button>
				</div>

				<div className={css(styleSheet.responseRow)}>
					{showSuccessScreen ? (
						<div className={css(styleSheet.successScreen)}>
							<h1>
								<SuccessCheckIcon /> Your Response has been posted
							</h1>
							{remainingReviewsCount ? <p>You have {remainingReviewsCount} more reviews ready for a reply</p> : null}
							<div className={css(baseStyleSheet.flex, baseStyleSheet.gap2)}>
								<Button onClick={() => setShowSuccessScreen(false)} label='See all reviews' kind='primary' />
								<Button onClick={closeModal} label='Close' kind='reverse' />
							</div>
						</div>
					) : null}

					{!showSuccessScreen ? (
						<>
							<section className={css(styleSheet.responseContainer)}>
								<h3 className={css(styleSheet.responseTitle)}>New Response</h3>

								{activeReview.autoReply?.autoPosted && !activeReview.autoReply.humanEdited ? (
									<p>
										{`This reply was posted automatically by AI on ${moment(activeReview.autoReply.decisionTime).format(
											'MMM D, YYYY'
										)}. You can edit or delete it.`}
									</p>
								) : null}

								<textarea value={response} onChange={onTextChanged} rows={9} className={css(styleSheet.textArea)} />

								{postGoogleBusinessReplyMutation.isError ? (
									<div className={css(styleSheet.errorContainer)}>
										{postGoogleBusinessReplyMutation.error?.systemMessage ||
											'There was an issue submitting your response. You can try again, or reach out to support.'}
									</div>
								) : null}

								{isReplying ? <LoadingSpinner type='small' /> : null}

								{!isReplying ? (
									<div className={css(styleSheet.buttonContainer)}>
										<button className={css(baseStyleSheet.ctaButton)} onClick={onPostReplyClicked} disabled={!response}>
											Post My Response
										</button>

										<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onCancel} disabled={isReplying}>
											Cancel
										</button>
									</div>
								) : null}
							</section>

							<GoogleReviewReplyAssistantChat
								key={activeReview.id}
								isOpen
								onUseGoogleReviewReply={(reply, sessionId) => {
									setResponse(reply);
									setChatSessionId(sessionId);
								}}
								context={{
									review: activeReview,
									companyName: userSession.account.companyName,
									reply: response,
								}}
							/>
						</>
					) : null}
				</div>
			</>
		);
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={open => {
				if (!open) {
					closeModal();
				}
			}}
			dismissOnOutsideClick={false}
		>
			<DialogPopup styles={[styleSheet.modal]} dialogCloseStyles={[styleSheet.closeButton]}>
				<DialogHeader styles={[styleSheet.header]}>
					<DialogTitle styles={[styleSheet.title]}>Google Review Response</DialogTitle>
				</DialogHeader>
				<DialogBody>{renderReviewContent()}</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};
